import { useState, useEffect } from "react";
import emailjs from "@emailjs/browser";

const ForgotPassword = ({ onBackToLogin }) => {
    const [username, setUsername] = useState("");
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [generatedOtp, setGeneratedOtp] = useState("");
    const [stage, setStage] = useState("username"); // username -> otp -> newPassword
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);
    const [resendDisabled, setResendDisabled] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);

    // Email.js configuration
    const EMAILJS_SERVICE_ID = "service_s30fimh";
    const EMAILJS_TEMPLATE_ID = "template_13hzuvh";
    const EMAILJS_PUBLIC_KEY = "-VKbzoApYY9_uofz1";

    useEffect(() => {
        // Start the entrance animation after a small delay
        const timer = setTimeout(() => {
            setFadeIn(true);
        }, 100);

        return () => {
            clearTimeout(timer);
        };
    }, []);

    // Generate a 6-digit OTP
    const generateOTP = () => {
        return Math.floor(100000 + Math.random() * 900000).toString();
    };

    // Validate email format
    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    // Handle send OTP button click
    const handleSendOtp = async () => {
        if (!username) {
            setErrorMessage("Please enter your username");
            return;
        }

        setIsLoading(true);
        setErrorMessage("");

        try {
            // Check if username exists in the database
            const response = await fetch('http://localhost:5000/api/auth/find-user-by-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: username }) // Keep using 'email' parameter for compatibility
            });

            if (!response.ok) {
                console.error("Server error:", response.status);
                throw new Error('Server error when checking username');
            }

            const data = await response.json();

            // If the username doesn't exist in the database, show error
            if (!data.exists) {
                setErrorMessage("No account found with this username");
                setIsLoading(false);
                return;
            }

            // Username exists, generate OTP
            const newOtp = generateOTP();
            setGeneratedOtp(newOtp);

            // Check if username is also an email, and send OTP if it is
            if (validateEmail(username)) {
                try {
                    // Initialize EmailJS with your public key
                    emailjs.init(EMAILJS_PUBLIC_KEY);

                    // Send OTP via email.js
                    const templateParams = {
                        to_email: username,
                        otp: newOtp,
                    };

                    await emailjs.send(
                        EMAILJS_SERVICE_ID,
                        EMAILJS_TEMPLATE_ID,
                        templateParams
                    );

                    setSuccessMessage(`OTP sent to your email (${username}). Please check your inbox.`);
                } catch (emailError) {
                    console.error("Email sending error:", emailError);
                    // Still show the OTP on screen for testing if email sending fails
                    setSuccessMessage(`Could not send email. Your OTP is: ${newOtp} (For testing purposes only)`);
                }
            } else {
                // Not an email, just show the OTP
                setSuccessMessage(`Your OTP is: ${newOtp} (For testing purposes only)`);
            }

            setStage("otp");

            // Start countdown for resend button (60 seconds)
            setResendDisabled(true);
            setResendCountdown(60);
            const countdownInterval = setInterval(() => {
                setResendCountdown(prevCount => {
                    if (prevCount <= 1) {
                        clearInterval(countdownInterval);
                        setResendDisabled(false);
                        return 0;
                    }
                    return prevCount - 1;
                });
            }, 1000);

        } catch (error) {
            console.error("Error:", error);
            setErrorMessage("An error occurred. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle resend OTP
    const handleResendOtp = async () => {
        setIsLoading(true);
        setErrorMessage("");
        setResendDisabled(true);

        try {
            // Generate a new OTP
            const newOtp = generateOTP();
            setGeneratedOtp(newOtp);

            // Check if username is also an email, and send OTP if it is
            if (validateEmail(username)) {
                try {
                    // Initialize EmailJS with your public key
                    emailjs.init(EMAILJS_PUBLIC_KEY);

                    // Send OTP via email.js
                    const templateParams = {
                        to_email: username,
                        otp: newOtp,
                    };

                    await emailjs.send(
                        EMAILJS_SERVICE_ID,
                        EMAILJS_TEMPLATE_ID,
                        templateParams
                    );

                    setSuccessMessage(`New OTP sent to your email (${username}). Please check your inbox.`);
                } catch (emailError) {
                    console.error("Email sending error:", emailError);
                    // Still show the OTP on screen for testing if email sending fails
                    setSuccessMessage(`Could not send email. Your new OTP is: ${newOtp} (For testing purposes only)`);
                }
            } else {
                // Not an email, just show the OTP
                setSuccessMessage(`Your new OTP is: ${newOtp} (For testing purposes only)`);
            }

            // Start countdown for resend button (60 seconds)
            setResendCountdown(60);
            const countdownInterval = setInterval(() => {
                setResendCountdown(prevCount => {
                    if (prevCount <= 1) {
                        clearInterval(countdownInterval);
                        setResendDisabled(false);
                        return 0;
                    }
                    return prevCount - 1;
                });
            }, 1000);

        } catch (error) {
            console.error("Error:", error);
            setErrorMessage("An error occurred while resending OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Verify OTP
    const verifyOtp = () => {
        if (!otp) {
            setErrorMessage("Please enter the OTP");
            return;
        }

        if (otp === generatedOtp) {
            setErrorMessage("");
            setSuccessMessage("OTP verified successfully!");
            setStage("newPassword");
        } else {
            setErrorMessage("Invalid OTP. Please try again.");
        }
    };

    // Handle password change
    const handleChangePassword = async () => {
        if (!newPassword || !confirmPassword) {
            setErrorMessage("Please fill in all fields");
            return;
        }

        if (newPassword.length < 8) {
            setErrorMessage("Password must be at least 8 characters long");
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMessage("Passwords do not match");
            return;
        }

        setIsLoading(true);
        setErrorMessage("");

        try {
            // Call the reset-password endpoint
            const response = await fetch('http://localhost:5000/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: username,
                    newPassword
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update password');
            }

            setSuccessMessage("Password changed successfully! Redirecting to login...");

            // Redirect back to login after a short delay
            setTimeout(() => {
                onBackToLogin("Password has been reset successfully");
            }, 2000);
        } catch (error) {
            console.error("Error changing password:", error);
            setErrorMessage("Failed to change password. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Progress indicator based on current stage
    const ProgressIndicator = () => {
        return (
            <div className="flex items-center justify-center mb-6">
                <div className="flex items-center w-full max-w-xs">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${stage === "username" || stage === "otp" || stage === "newPassword"
                            ? "bg-black dark:bg-uasYellow text-white dark:text-black"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300"
                        } font-semibold text-sm`}>
                        1
                    </div>
                    <div className={`flex-1 h-1 ${stage === "otp" || stage === "newPassword"
                            ? "bg-black dark:bg-uasYellow"
                            : "bg-gray-200 dark:bg-gray-700"
                        }`}></div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${stage === "otp" || stage === "newPassword"
                            ? "bg-black dark:bg-uasYellow text-white dark:text-black"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300"
                        } font-semibold text-sm`}>
                        2
                    </div>
                    <div className={`flex-1 h-1 ${stage === "newPassword"
                            ? "bg-black dark:bg-uasYellow"
                            : "bg-gray-200 dark:bg-gray-700"
                        }`}></div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${stage === "newPassword"
                            ? "bg-black dark:bg-uasYellow text-white dark:text-black"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300"
                        } font-semibold text-sm`}>
                        3
                    </div>
                </div>
            </div>
        );
    };
    // Custom button component
    const Button = ({ onClick, disabled, isPrimary, className, children }) => {
        return (
            <button
                onClick={onClick}
                disabled={disabled}
                className={`py-3 px-6 rounded-lg font-medium transition-all duration-200 flex items-center justify-center
                  ${isPrimary
                        ? "bg-black dark:bg-uasYellow hover:bg-uasYellow hover:text-black dark:hover:bg-yellow-300 text-white dark:text-black"
                        : "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white"}
                  ${disabled ? "opacity-60 cursor-not-allowed" : ""}
                  ${className || ""}`}
            >
                {children}
            </button>
        );
    };

    return (
        <div className={`relative w-full max-w-md bg-white dark:bg-laryzeDarkGray rounded-2xl shadow-xl 
                    p-8 border-t-4 border-black dark:border-uasYellow
                    backdrop-blur-md bg-opacity-80 dark:bg-opacity-80
                    transition-all duration-700 ease-out 
                    ${fadeIn ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>

            {/* Logo or Icon could go here */}
            <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-black dark:bg-uasYellow flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white dark:text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                </div>
            </div>

            <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-1">
                    {stage === "username" && "Forgot Password"}
                    {stage === "otp" && "Verify OTP"}
                    {stage === "newPassword" && "Reset Password"}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {stage === "username" && "Enter your username to reset password"}
                    {stage === "otp" && "Enter the OTP sent to your email"}
                    {stage === "newPassword" && "Create a new secure password"}
                </p>
            </div>

            {/* Progress indicators */}
            <ProgressIndicator />

            {/* Error and success messages */}
            {errorMessage && (
                <div className="mb-6 text-center p-3 bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 dark:border-red-500 text-red-700 dark:text-red-300 rounded">
                    <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{errorMessage}</span>
                    </div>
                </div>
            )}

            {successMessage && (
                <div className="mb-6 text-center p-3 bg-green-50 dark:bg-green-900/30 border-l-4 border-green-500 dark:border-green-500 text-green-700 dark:text-green-300 rounded">
                    <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{successMessage}</span>
                    </div>
                </div>
            )}

            <div className="space-y-4">
                {/* Username Input Stage */}
                {stage === "username" && (
                    <>
                        <div className="relative mb-4">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500 dark:text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Enter your username or email"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                disabled={isLoading}
                                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 
                          bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-30
                          text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-400
                          focus:ring-2 focus:ring-uasYellow focus:border-transparent dark:focus:ring-uasYellow
                          transition-all duration-200"
                            />
                        </div>
                        <Button
                            onClick={handleSendOtp}
                            disabled={isLoading}
                            isPrimary={true}
                            className="w-full"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white dark:text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sending OTP...
                                </>
                            ) : (
                                <>
                                    Continue
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </>
                            )}
                        </Button>
                    </>
                )}

                {/* OTP Verification Stage */}
                {stage === "otp" && (
                    <>
                        <div className="relative mb-4">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500 dark:text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Enter 6-digit OTP"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                maxLength={6}
                                disabled={isLoading}
                                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 
                          bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-30
                          text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-400
                          focus:ring-2 focus:ring-uasYellow focus:border-transparent dark:focus:ring-uasYellow
                          transition-all duration-200"
                            />
                        </div>
                        <div className="flex justify-between gap-4">
                            <Button
                                onClick={() => {
                                    setStage("username");
                                    setSuccessMessage("");
                                }}
                                isPrimary={false}
                                className="flex-1"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back
                            </Button>
                            <Button
                                onClick={verifyOtp}
                                disabled={isLoading}
                                isPrimary={true}
                                className="flex-1"
                            >
                                Verify OTP
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </Button>
                        </div>

                        {/* Resend OTP button */}
                        <div className="mt-4 text-center">
                            <button
                                onClick={handleResendOtp}
                                disabled={resendDisabled || isLoading}
                                className={`text-sm transition-all duration-200 flex items-center justify-center mx-auto
                          ${resendDisabled
                                        ? "text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                        : "text-black dark:text-uasYellow hover:underline"}`}
                            >
                                {resendDisabled ? (
                                    <>
                                        Resend OTP in {resendCountdown}s
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Resend OTP
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}

                {/* New Password Stage */}
                {stage === "newPassword" && (
                    <>
                        <div className="relative mb-4">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500 dark:text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <input
                                type="password"
                                placeholder="New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={isLoading}
                                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 
                          bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-30
                          text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-400
                          focus:ring-2 focus:ring-uasYellow focus:border-transparent dark:focus:ring-uasYellow
                          transition-all duration-200"
                            />
                        </div>
                        <div className="relative mb-4">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500 dark:text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <input
                                type="password"
                                placeholder="Confirm Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={isLoading}
                                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 
                          bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-30
                          text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-400
                          focus:ring-2 focus:ring-uasYellow focus:border-transparent dark:focus:ring-uasYellow
                          transition-all duration-200"
                            />
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                            Password must be at least 8 characters long
                        </div>
                        <div className="flex justify-between gap-4">
                            <Button
                                onClick={() => {
                                    setStage("otp");
                                    setSuccessMessage("");
                                }}
                                isPrimary={false}
                                className="flex-1"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back
                            </Button>
                            <Button
                                onClick={handleChangePassword}
                                disabled={isLoading}
                                isPrimary={true}
                                className="flex-1"
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white dark:text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        Reset Password
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </>
                                )}
                            </Button>
                        </div>
                    </>
                )}

                {/* Back to Login */}
                <div className="mt-8 text-center border-t border-gray-200 dark:border-gray-700 pt-6">
                    <button
                        onClick={() => onBackToLogin()}
                        className="text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white text-sm flex items-center justify-center mx-auto transition-colors duration-200"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                        </svg>
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;